import { ContentState } from "draft-js";
import { Api } from "@core/api/api";
import { organizationHeaders } from "@core/api/organizationHeaders";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Article, ArticleDraft, ArticleDuration, ArticleStatus } from "./article";
import { articleDraftToDto, ArticleDto, articleFromDto, DetailedArticle } from "./dtos";
import { FeedbackDto, feedbackFromDto } from "@domain/lms/article/feedback/dto";
import { Feedback, FeedbackInput } from "@domain/lms/article/feedback/feedback";

const ARTICLE_URL = "/articles";
const ARTICLE_INSIGHTFUL_URL = "/instructives";

export class ArticleApi {
	constructor(private api: Api) {}

	async get(id: string): Promise<Article> {
		if (id.charAt(0) === "@") id = id.substring(1);
		const result = await this.api.get<ArticleDto>(`${ARTICLE_URL}/${id}`);
		return articleFromDto(result.data);
	}
	async getBySlug(slug: string): Promise<Article> {
		const result = await this.api.get<ArticleDto>(`${ARTICLE_URL}?slug=${slug}`);
		return articleFromDto(result.data);
	}
	async getDetailed(id: string): Promise<DetailedArticle> {
		if (id.charAt(0) === "@") id = id.substring(1);
		const result = await this.api.get<ArticleDto>(`${ARTICLE_URL}/${id}/content`);
		return articleFromDto(result.data);
	}

	async getDetailedBySlug(slug: string): Promise<DetailedArticle> {
		const result = await this.api.get<ArticleDto>(`${ARTICLE_URL}?slug=${slug}`);
		return articleFromDto(result.data);
	}

	async getSuggestions(pageOptions: PaginationOptions, categories: Category[]): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}/suggested?${pageParams(pageOptions)}&categories=${categories.join(",")}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async getNewsArticles(count: number, page: number): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(`${ARTICLE_URL}/?page=${page}&size=${count}&articleType=NEWS`);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	delete(articleId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${ARTICLE_URL}/${articleId}`, organizationHeaders(organizationId));
	}

	async getFavorites(pageOptions: PaginationOptions): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(`${ARTICLE_URL}?filter=favorite&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async getProfileArticles(profileId: string, pageOptions: PaginationOptions): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}?userId=${profileId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async getOrganizationArticles(organizationId: string, pageOptions: PaginationOptions): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}?organizationId=${organizationId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async createArticle(draft: ArticleDraft, organizationId?: string): Promise<DetailedArticle> {
		const result = await this.api.post<ArticleDto>(
			ARTICLE_URL,
			articleDraftToDto(draft),
			organizationHeaders(organizationId)
		);
		return articleFromDto(result.data);
	}

	async saveArticle(draft: ArticleDraft, id: string, organizationId?: string): Promise<DetailedArticle> {
		const result = await this.api.put<ArticleDto>(
			`${ARTICLE_URL}/${id}`,
			articleDraftToDto(draft),
			organizationHeaders(organizationId)
		);
		return articleFromDto(result.data);
	}

	async setFavorite(id: string): Promise<void> {
		await this.api.post<void>(`${ARTICLE_URL}/${id}/favorite`);
	}

	async removeFavorite(id: string): Promise<void> {
		await this.api.delete<void>(`${ARTICLE_URL}/${id}/favorite`);
	}

	async setInstructive(id: string): Promise<void> {
		return this.api.post(`${ARTICLE_INSIGHTFUL_URL}/${id}${ARTICLE_URL}`);
	}

	async removeInstructive(id: string): Promise<void> {
		return this.api.delete(`${ARTICLE_INSIGHTFUL_URL}/${id}${ARTICLE_URL}`);
	}

	async share(articleId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${ARTICLE_URL}/${articleId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	view(articleId: string): Promise<void> {
		return this.api.post(`${ARTICLE_URL}/${articleId}/view`);
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: ArticleDuration[]
	): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations
			)}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async sendFeedback(articleId: string, feedback: FeedbackInput): Promise<void> {
		await this.api.post(`${ARTICLE_URL}/${articleId}/feedbacks`, feedback);
	}

	async getMyFeedback(articleId: string, articleFeedbackId: string): Promise<Feedback | null> {
		const result = await this.api.get<FeedbackDto>(`${ARTICLE_URL}/${articleId}/feedbacks/${articleFeedbackId}`);
		return result.data && feedbackFromDto(result.data);
	}

	async listFeedbacks(articleId: string, pageOptions: PaginationOptions): Promise<Page<Feedback>> {
		const result = await this.api.get<Page<FeedbackDto>>(
			`${ARTICLE_URL}/${articleId}/feedbacks?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((dto) => feedbackFromDto(dto)) };
	}

	async deleteFeedback(articleId: string, articleFeedbackId: string): Promise<void> {
		await this.api.delete(`${ARTICLE_URL}/${articleId}/feedbacks/${articleFeedbackId}`);
	}

	async updateFeedback(articleId: string, articleFeedbackId: string, feedback: FeedbackInput): Promise<void> {
		await this.api.put(`${ARTICLE_URL}/${articleId}/feedbacks/${articleFeedbackId}`, feedback);
	}

	async getMyArticles(status: ArticleStatus | "ALL", pageOptions: PaginationOptions): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}?filter=mine&status=${status === "ALL" ? "" : status}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}

	async getWorkspaceArticles(workspaceId: string, pageOptions: PaginationOptions): Promise<Page<Article>> {
		const result = await this.api.get<Page<ArticleDto>>(
			`${ARTICLE_URL}?workspaceId=${workspaceId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => articleFromDto(c)) };
	}
}
